import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Copy } from "lucide-react";
import { routes } from "../../../configs/constants/routes";
import { InvoiceBatchExportSummary } from "../../../models/InvoiceBatch";

interface Props {
	/** Open items, counted from the batch's invoices. */
	counts: InvoiceBatchExportSummary;
	/**
	 * Some failed invoice's time reports are free again (a clean failure or a
	 * held one marked "Finns inte i Visma"), so a re-run with the same
	 * selection picks them up.
	 */
	hasReleasedTimeReports: boolean;
	/** Source batch of "Ny körning med samma urval". */
	invoiceBatchId: string;
}

/**
 * Callout on a processed batch whose export left per-invoice follow-ups:
 * failed invoices, invoices to check in Visma and missing specifications.
 * Renders nothing when there is nothing to follow up.
 */
const InvoiceBatchExportBanner: React.FC<Props> = ({
	counts,
	hasReleasedTimeReports,
	invoiceBatchId,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const lines = [
		counts.failed > 0 && t("invoiceBatch.exportBanner.failed", { count: counts.failed }),
		counts.held > 0 && t("invoiceBatch.exportBanner.held", { count: counts.held }),
		counts.attachmentMissing > 0 &&
			t("invoiceBatch.exportBanner.attachmentMissing", { count: counts.attachmentMissing }),
	].filter(Boolean) as string[];

	// Every failure resolved, but released time reports still wait for a re-run
	if (!counts.failed && hasReleasedTimeReports) lines.push(t("invoiceBatch.exportBanner.released"));

	if (!lines.length) return null;

	const isSevere = counts.failed > 0 || counts.held > 0;

	return (
		<div
			role="status"
			className={
				isSevere
					? "mb-3 flex items-start justify-between gap-4 rounded-md border-2 border-solid border-red-300 bg-red-50 p-4"
					: "mb-3 flex items-start justify-between gap-4 rounded-md border-2 border-solid border-amber-300 bg-amber-50 p-4"
			}
		>
			<div className="flex flex-col gap-1">
				{lines.map((line) => (
					<p key={line} className="m-0 text-sm text-gray-800">
						{line}
					</p>
				))}
			</div>

			{hasReleasedTimeReports && (
				<button
					type="button"
					// Same target as the list's "Ny körning med samma urval": the
					// create form seeds its customer selection from this batch
					onClick={() => navigate(`${routes.INVOICE_BATCH_CREATE}?from=${invoiceBatchId}`)}
					className="flex shrink-0 items-center gap-1.5 rounded-md border border-solid border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50"
				>
					<Copy size={14} />
					{t("invoiceBatch.newLikeThis")}
				</button>
			)}
		</div>
	);
};

export default InvoiceBatchExportBanner;
